import React, { ReactNode, useCallback, useState } from "react";
import { Platform, StyleSheet, View } from "react-native";
import { useRouter } from "expo-router";
import {
  AppTitle,
  Logo,
  NotificationCard,
  ScreenLayout,
  ScrollView,
  Spinner,
  Text,
  TextButton,
  TextInput
} from "@/components";
import { brand, layout } from "@/constants";
import { joinWaitlist } from "@/utils/serenity-api-client";

/** Wraps children in a <form> on web to enable Enter-to-submit */
function FormWrapper({
  onSubmit,
  children
}: {
  onSubmit: () => void;
  children: ReactNode;
}) {
  if (Platform.OS !== "web") return <>{children}</>;
  return (
    <form
      onSubmit={(e: React.FormEvent) => {
        e.preventDefault();
        onSubmit();
      }}
      style={{ display: "contents" }}
    >
      {children}
    </form>
  );
}

/** Hidden honeypot input — rendered on web only, ignored by humans, filled by bots */
function HoneypotInput({ onChange }: { onChange: (v: string) => void }) {
  if (Platform.OS !== "web") return null;
  return (
    <input
      type="text"
      name="website"
      tabIndex={-1}
      autoComplete="off"
      aria-hidden="true"
      onChange={(e) => onChange(e.target.value)}
      style={{
        position: "absolute",
        left: "-10000px",
        width: "1px",
        height: "1px",
        opacity: 0,
        pointerEvents: "none"
      }}
    />
  );
}

/**
 * Waitlist screen — collects an email from visitors who don't yet have access.
 * No authentication required; writes directly to the public /waitlist endpoint.
 */
export default function WaitlistScreen() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(async () => {
    const trimmed = email.trim();
    setError(null);
    if (!trimmed) {
      setError("Please enter your email address.");
      return;
    }
    setIsSubmitting(true);
    const result = await joinWaitlist(trimmed, honeypot);
    setIsSubmitting(false);
    if (result.success) {
      setSubmittedEmail(trimmed);
    } else {
      setError(result.message);
    }
  }, [email, honeypot]);

  return (
    <ScreenLayout>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <Logo width={72} height={72} style={styles.logo} />
        <AppTitle style={styles.appTitle} />

        {submittedEmail ? (
          <View style={styles.cardContainer}>
            <NotificationCard icon="check_circle">
              <Text
                variant="regular"
                size="small"
                color="primary"
                style={styles.cardText}
              >
                {"You're on the waitlist! We'll email "}
                <Text variant="medium" size="small" color="primary">
                  {submittedEmail}
                </Text>
                {" when your spot is ready."}
              </Text>
            </NotificationCard>
          </View>
        ) : (
          <>
            <Text style={styles.subtitle} color="secondary">
              {brand.name} is currently available by invitation.{"\n"}
              {"Join the waitlist and we'll email you when your spot opens."}
            </Text>

            <FormWrapper onSubmit={handleSubmit}>
              <View style={styles.formContainer}>
                <TextInput
                  placeholder="Email"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoComplete="email"
                  value={email}
                  onChangeText={setEmail}
                  editable={!isSubmitting}
                />
                <HoneypotInput onChange={setHoneypot} />
              </View>

              {error && (
                <View style={styles.cardContainer}>
                  <NotificationCard
                    variant="error"
                    label="Error"
                    message={error}
                  />
                </View>
              )}

              <View style={styles.buttonRow}>
                <TextButton
                  buttonText={isSubmitting ? "Joining..." : "Join the waitlist"}
                  onPress={handleSubmit}
                  disabled={isSubmitting}
                />
              </View>
            </FormWrapper>

            {isSubmitting && <Spinner size="small" style={styles.spinner} />}
          </>
        )}

        <Text
          style={styles.link}
          color="secondary"
          onPress={() => router.replace("/(drawer)/login")}
        >
          Already have access? Sign in
        </Text>
      </ScrollView>
    </ScreenLayout>
  );
}

const styles = StyleSheet.create({
  scrollContent: {
    flexGrow: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingTop: layout.space.xl,
    paddingBottom: layout.space.lg
  },
  logo: {
    marginBottom: layout.space.sm
  },
  appTitle: {
    marginBottom: layout.space.xl * 2
  },
  subtitle: {
    textAlign: "center",
    lineHeight: layout.size.lg,
    marginBottom: layout.space.xl * 2,
    paddingHorizontal: layout.size.lg
  },
  formContainer: {
    width: "100%",
    maxWidth: layout.minWidth,
    paddingHorizontal: layout.space.lg,
    gap: layout.space.sm
  },
  buttonRow: {
    alignItems: "center",
    marginTop: layout.space.md
  },
  spinner: {
    marginTop: layout.space.xs
  },
  link: {
    marginTop: layout.space.md,
    textDecorationLine: "underline"
  },
  cardContainer: {
    width: "100%",
    paddingHorizontal: layout.space.lg,
    marginTop: layout.space.md
  },
  cardText: {
    flex: 1
  }
});
